import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";

/**
 * Render a dialogue script with each {{position}} blank replaced by a token
 * showing the user's answer vs. the correct answer:
 *   correct    → green pill with ✓
 *   wrong      → red pill (strike-through) ✗ + green dashed correct answer
 *   unanswered → grey "(blank)" ✗ + green dashed correct answer
 *
 * Inputs:
 *   - dialogueItem: { userAnswer: { [pos]: string }, question?: { dialogueScript, blanks } }
 *   - dialogueScript / blanks: optional overrides (defaults are read from dialogueItem.question)
 */
const DialogueScriptReview = ({ dialogueItem, dialogueScript, blanks }) => {
  const script = dialogueScript || dialogueItem?.question?.dialogueScript || "";
  const blankList = blanks || dialogueItem?.question?.blanks || [];
  const userAnswers = dialogueItem?.userAnswer || {};

  const html = useMemo(() => {
    let out = (script || "").replace(/\n/g, "<br/>");
    blankList.forEach((blank) => {
      const position = blank.position;
      const raw =
        userAnswers[position] ?? userAnswers[String(position)] ?? "";
      const userAns = (raw ?? "").toString();
      const correctAnswer = blank.correctAnswers?.[0] || "";
      const isCorrect =
        userAns &&
        blank.correctAnswers?.some(
          (a) =>
            a.toString().toLowerCase().trim() ===
            userAns.toLowerCase().trim()
        );

      let replacement;
      if (!userAns) {
        replacement =
          `<span class="blank-box blank-empty">(blank)</span> ` +
          `<span style="color:#d32f2f;font-weight:700;">✗</span> ` +
          `<span class="blank-box blank-answer">${correctAnswer}</span>`;
      } else if (isCorrect) {
        replacement = `<span class="blank-box blank-correct">${userAns} <span style="color:#2e7d32;">✓</span></span>`;
      } else {
        replacement =
          `<span class="blank-box blank-wrong"><span style="text-decoration:line-through;">${userAns}</span> ` +
          `<span style="color:#d32f2f;">✗</span></span> ` +
          `<span class="blank-box blank-answer">${correctAnswer}</span>`;
      }

      out = out.split(`{{${position}}}`).join(replacement);
    });
    return out;
  }, [script, blankList, userAnswers]);

  if (!script) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        backgroundColor: "#fafafa",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Dialogue Script
      </Typography>
      <Box
        sx={{
          fontSize: "1.05rem",
          lineHeight: 2.4,
          whiteSpace: "pre-wrap",
          "& .blank-box": {
            display: "inline-block",
            lineHeight: 1,
            padding: "4px 6px",
            margin: "0 2px",
            fontWeight: 600,
            verticalAlign: "middle",
            borderRadius: "4px",
          },
          "& .blank-correct": {
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            border: "1px solid #4caf50",
          },
          "& .blank-wrong": {
            backgroundColor: "#ffebee",
            color: "#d32f2f",
            border: "1px solid #f44336",
          },
          "& .blank-empty": {
            backgroundColor: "#f5f5f5",
            border: "2px dashed #bdbdbd",
            color: "#9e9e9e",
            minWidth: "70px",
            textAlign: "center",
          },
          "& .blank-answer": {
            color: "#2e7d32",
            border: "1px dashed #4caf50",
            backgroundColor: "#f1f8e9",
          },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Paper>
  );
};

export default DialogueScriptReview;
